import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@progress/kendo-react-buttons';
import { Invitation } from 'services/api/invitations/types';
import { formatDate } from 'utils/date';
import { useNotificationsContext } from 'context';
import { api } from 'services/api';
import { ErrorNotification } from 'app/components/Notifications';
import { Success } from 'app/components/Notifications';
import { DeleteDialog } from 'app/components/dialogs';

interface IPendingSentInvitationRow {
  isMobile: boolean;
  invitation: Invitation;
}

function PendingSentInvitationRow(props: IPendingSentInvitationRow) {
  const { invitation } = props;
  const { addNotification } = useNotificationsContext();
  const [resendInvitation, { isLoading: isResendInvitationLoading }] =
    api.useResendInvitationMutation();
  const [revokeInvitation] = api.useRevokeInvitationMutation();
  const [isRevokeAccessDialogOpen, setIsRevokeAccessDialogOpen] =
    useState(false);

  const isLoading = isResendInvitationLoading;

  const invitedAt = formatDate(
    invitation.accessDetails.invitedAt,
    'DATE_FORMAT_COMPACT',
  );
  const [startsAtDate, startsAtTime] = invitation.accessDetails.startsAt
    ? formatDate(
        invitation.accessDetails.startsAt,
        'DATE_FORMAT_SPLIT_TIME',
      ).split('|')
    : [];
  const [endsAtDate, endsAtTime] = invitation.accessDetails.endsAt
    ? formatDate(
        invitation.accessDetails.endsAt,
        'DATE_FORMAT_SPLIT_TIME',
      ).split('|')
    : [];

  // TODO replace with a new hook, like usePrefixedTranslation(namespace: string, prefix: string)
  const { t } = useTranslation();
  const translationKey =
    'access_sharing.pending_received_invitations.table.actions';

  const handleResentInvitation = async () => {
    const { accessDetails, sharedItemDetails } = invitation;

    try {
      await resendInvitation({
        shareType: sharedItemDetails.shareType,
        exposedId: [sharedItemDetails.exposedId],
        sharedWith: [
          {
            email: accessDetails.sharedWithEmail!,
            role: accessDetails.sharedRole,
            endsAt: accessDetails.endsAt,
            startsAt: accessDetails.startsAt,
          },
        ],
      });
      addNotification(
        <Success
          text={t('access_sharing.notifications.invitation-successfully-sent')}
        />,
      );
    } catch (error) {
      addNotification(<ErrorNotification text={'Server error'} />);
      console.error(error);
    }
  };

  const handleRevokeAccess = async () => {
    try {
      await revokeInvitation([
        {
          shareType: invitation.sharedItemDetails.shareType,
          exposedAccessSharingRecordId: invitation.exposedAccessSharingRecordId,
        },
      ]);

      addNotification(
        <Success text={t('access_sharing.snackbars.access-revoked')} />,
      );
    } catch (error) {
      console.error(error);
      addNotification(<ErrorNotification text={'Server error'} />);
    } finally {
      setIsRevokeAccessDialogOpen(false);
    }
  };

  return (
    <>
      <DeleteDialog
        open={isRevokeAccessDialogOpen}
        title={t('revoke_access_confirmation', {
          email: invitation.accessDetails.sharedWithEmail,
          machineName: invitation.sharedItemDetails.sharedItemLabel,
        })}
        confirmationLabel={t('access_sharing.role_based_actions.revoke')}
        onClose={() => setIsRevokeAccessDialogOpen(false)}
        onConfirm={handleRevokeAccess}
      />

      {!props.isMobile ? (
        <div>
          <div className="access-sharing-table-row pending-sent-invitation">
            <div className="equipment-table-field ellipsis">
              {invitation.accessDetails.sharedWithEmail}
            </div>
            <div className="equipment-table-field ellipsis">
              {invitation.sharedItemDetails.sharedItemLabel}
            </div>
            <div className="equipment-table-field">
              {invitation.accessDetails.sharedRole}
            </div>
            <div className="pending-details min-w-0">
              <span className="equipment-table-field ellipsis">
                {invitation.accessDetails.invitedByName}
              </span>
              <span className="grayed-text">{invitedAt}</span>
            </div>
            <div className="pending-details">
              <span>{startsAtDate}</span>
              <span>{startsAtTime}</span>
            </div>
            <div className="pending-details">
              <span>{endsAtDate}</span>
              <span>{endsAtTime}</span>
            </div>
            <div className="pending-invitation-buttons-container">
              <Button
                className="pending-invitation-button"
                disabled={isLoading}
                onClick={() => setIsRevokeAccessDialogOpen(true)}
              >
                {t(`${translationKey}.revoke`)}
              </Button>
              <Button
                className="pending-invitation-button"
                themeColor="tertiary"
                onClick={handleResentInvitation}
                disabled={isLoading}
              >
                {t(`${translationKey}.resend`)}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="pending-invitation-card">
          <div className="title-and-date sent-invitation">
            <div className="pending-invitation-title">
              {invitation.accessDetails.sharedRole}
            </div>
            <div className="pending-invitation-date">{invitedAt}</div>
          </div>
          <div className="pending-invitation-email">
            {invitation.accessDetails.sharedWithEmail}
          </div>
          <div className="access-to-container">
            <div className="bolded-info">{t('access_to')}</div>
            <div>{invitation.sharedItemDetails.sharedItemLabel}</div>
          </div>
          <div className="invited-by-container">
            <span className="bolded-info">{t('invited_by')}: </span>
            <span>{invitation.accessDetails.invitedByName}</span>
          </div>
          <div className="pending-invitation-buttons-container">
            <Button
              className="pending-invitation-button"
              disabled={isLoading}
              onClick={() => setIsRevokeAccessDialogOpen(true)}
            >
              {t(`${translationKey}.revoke`)}
            </Button>
            <Button
              className="pending-invitation-button"
              themeColor="tertiary"
              onClick={handleResentInvitation}
              disabled={isLoading}
            >
              {t(`${translationKey}.resend`)}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

export default PendingSentInvitationRow;
